import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError
} from "@reduxjs/toolkit/query/react";

import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import { differenceInMinutes } from "date-fns";

// type-only: a value import would make ts-jest (per-file transpile) require the whole store
import type { RootState } from "app/store";
// Depends on leads-ui slice for comms auth state (access_token, refresh_token)
// and actions (setAuthTokenInfo, logoutSunoComms). Do not change without updating store.
import {
  logoutSunoComms,
  setAuthTokenInfo
} from "features/leads-ui/leads-ui.slice";
import { viteEnv } from "utils/vite-env.util";

import type { SunoCommsTokenPayload } from "../types/suno-comms-auth.type";

export const removeSunoCommsCachedAuthData = () => {
  localStorage.removeItem("suno_comms_token");
  localStorage.removeItem("suno_comms_refresh_token");
  localStorage.removeItem("suno_comms_token_expiration");
};

export const refreshJwt = async (refresh_token?: string) => {
  const storedRefresh = localStorage.getItem("suno_comms_refresh_token");
  const tokenToUse = storedRefresh ?? refresh_token;
  if (!tokenToUse) {
    return { errorMessage: "No refresh token" };
  }
  try {
    const response: any = await fetch(
      `${viteEnv.VITE_SUNO_COMMS_API}/auth/jwt/refresh`,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ refresh_token: tokenToUse })
      }
    );
    const data = await response.json();
    const accessToken = data.access_token;
    const refreshToken = data.refresh_token;

    const decodedPayload =
      accessToken && typeof accessToken === "string"
        ? decodeToken(accessToken)
        : undefined;

    if (response.status.toString().match(/2\d{2}/) && decodedPayload) {
      const expirationDate = new Date(decodedPayload.exp * 1000);
      const expirationTimestamp = expirationDate.getTime().toString();

      localStorage.setItem("suno_comms_token", accessToken);
      localStorage.setItem("suno_comms_refresh_token", refreshToken);
      localStorage.setItem("suno_comms_token_expiration", expirationTimestamp);

      return {
        accessToken,
        refreshToken,
        expirationTimestamp,
        expiresAt: expirationDate.toISOString()
      };
    } else {
      removeSunoCommsCachedAuthData();
      const errorMessage = "Invalid user api_key";
      return { errorMessage };
    }
  } catch (err: any) {
    removeSunoCommsCachedAuthData();
    const errorMessage =
      "Something went wrong. Please contact your support and describe your issue.";
    return { errorMessage };
  }
};

export const isTokenExpired = (expiration_date: string): boolean => {
  try {
    const expirationDate = new Date(expiration_date);
    const currentDate = new Date();
    return expirationDate <= currentDate;
  } catch (error) {
    return true;
  }
};

export const decodeToken = (token: string) => {
  if (!token || typeof token !== "string") return undefined;
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return undefined;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    return JSON.parse(jsonPayload) as SunoCommsTokenPayload;
  } catch (error) {
    return undefined;
  }
};

export const shouldRefreshToken = (token?: string) => {
  if (!token) return false;

  const decodedPayload = decodeToken(token);

  if (!decodedPayload?.exp) return false;

  const expirationDate = new Date(decodedPayload.exp * 1000);
  return differenceInMinutes(expirationDate, new Date()) <= 1;
};

export const prepareHeaders = (headers: Headers, { getState }: any) => {
  const token = (getState() as RootState).leads.access_token;

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  return headers;
};

const baseQueryFn = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    prepareHeaders
  });

export const commsBaseQueryWithJWTReauthFn = (baseUrl: string) => {
  const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
  > = async (args, api, extraOptions) => {
    const { access_token, refresh_token } = (api.getState() as RootState).leads;

    let isError = false;
    let result: any = undefined;

    const shouldRefresh = shouldRefreshToken(access_token);

    if (shouldRefresh) {
      const { errorMessage, accessToken, refreshToken, expiresAt } =
        await refreshJwt(
          String(
            localStorage.getItem("suno_comms_refresh_token") || refresh_token
          )
        );

      isError = Boolean(errorMessage);

      if (!isError) {
        api.dispatch(
          setAuthTokenInfo({
            access_token: accessToken,
            refresh_token: refreshToken,
            access_token_expires_at: expiresAt
          })
        );
        result = await baseQueryFn(baseUrl)(args, api, extraOptions);
        isError =
          result.error &&
          (result.error.status === 403 || result.error.status === 401);
      }
    } else {
      result = await baseQueryFn(baseUrl)(args, api, extraOptions);
      isError =
        result.error &&
        (result.error.status === 403 || result.error.status === 401);
    }

    if (isError && access_token) {
      api.dispatch(logoutSunoComms());
      console.log("Suno Comms Access Denied");
    }

    return result;
  };

  return baseQueryWithReauth;
};
